// @ts-strict-check
import React, { useMemo, useState } from 'react';
import { LoadingSpinner, ErrorMessage, ToggleButton, EmptyState } from '../components/ui';
import { useMarketPulseData } from '../hooks/useClubSections';
import { useDeskAdmin } from '../hooks/useDeskAdmin';
import MarketPulseComingSoon from '../components/marketPulse/MarketPulseComingSoon';
import { TimePeriod } from '../types/api';
import { FileText } from 'lucide-react';
import MarketPulseProductDocModal from '../components/marketPulse/MarketPulseProductDocModal';
import MarketPulseKpiSection from '../components/marketPulse/MarketPulseKpiSection';
import MarketPulseTableSection from '../components/marketPulse/MarketPulseTableSection';
import MarketPulseTrendSection from '../components/marketPulse/MarketPulseTrendSection';
import {
  computeMarketPulseComparisonData,
  computeMarketPulseYoYComparisonData,
  hasMarketPulseYoYData,
} from '../components/marketPulse/marketPulseComparisonData';
import MarketPulseYoYTableSection from '../components/marketPulse/MarketPulseYoYTableSection';
import {
  PulseRow,
  MARKET_PULSE_DASHBOARD,
} from '../components/marketPulse/marketPulseConstants';
import { isMarketPulseComingSoonPreview } from '../utils/marketPulsePreview';

/**
 * نبض بازار (Market Pulse)
 * --------------------------------------------------------------------------
 * مقایسه‌ی عملکرد شعبه با «کل بازار» روی ۴ شاخص کلیدی:
 *   1. رشد میزان فروش   (رشد ماه‌به‌ماه مبلغ فروش)
 *   2. رشد تعداد سفارش  (رشد ماه‌به‌ماه تعداد سفارش)
 *   3. رشد قیمت         (رشد ماه‌به‌ماه میانگین قیمت هر کالا)
 *   4. رشد سبد          (رشد ماه‌به‌ماه میانگین تعداد اقلام در هر سفارش)
 *
 * منبع داده: همان «داشبورد جامع باشگاه مشتریان»
 *   - branchData                        → شعبه
 *   - allRestaurantsData                → بازار (strict)
 *   - allRestaurantsPartialLatestData   → بازار (ماه جاری، فیلتر شل‌تر)
 */

const MarketPulseIntroBanner: React.FC<{ onOpenProductDoc: () => void }> = ({ onOpenProductDoc }) => (
  <p
    className="flex items-start gap-2 text-xs md:text-sm text-text-secondary bg-bg-secondary border border-border-primary rounded-lg px-3 py-2.5 max-w-2xl mx-auto leading-relaxed"
    role="note"
  >
    <FileText className="w-4 h-4 text-accent-purple shrink-0 mt-0.5" strokeWidth={2} aria-hidden="true" />
    <span>
      {MARKET_PULSE_DASHBOARD.introBannerBeforeLink}{' '}
      <button
        type="button"
        onClick={onOpenProductDoc}
        className="font-semibold text-accent-purple underline underline-offset-2 decoration-accent-purple/50 hover:text-accent-purple/80 hover:decoration-accent-purple transition-colors focus:outline-none focus:ring-2 focus:ring-accent-purple rounded-sm"
      >
        {MARKET_PULSE_DASHBOARD.introBannerLink}
      </button>
      {' '}{MARKET_PULSE_DASHBOARD.introBannerAfterLink}
    </span>
  </p>
);

interface MarketPulseDashboardProps {
  branchId?: number;
  currency?: string;
  customTimeRange?: TimePeriod;
}

const MarketPulseDashboard: React.FC<MarketPulseDashboardProps> = ({ branchId = 0 }) => {
  const { isAdmin, isLoading: isAdminLoading } = useDeskAdmin();
  const forceComingSoonPreview = isMarketPulseComingSoonPreview();

  const customTimeRange: TimePeriod = '12months';
  const canLoadAdminDashboard = isAdmin && !forceComingSoonPreview;
  const { data, isLoading, error, refetch } = useMarketPulseData(
    canLoadAdminDashboard ? branchId : undefined,
    customTimeRange,
    canLoadAdminDashboard,
  );

  const [extrapolateToFullMonth, setExtrapolateToFullMonth] = useState<boolean>(false);
  const [isProductDocumentOpen, setIsProductDocumentOpen] = useState<boolean>(false);

  const processed = useMemo(() => {
    const branchAll = ((data?.branchData as PulseRow[]) || []);
    const marketAll = ((data?.allRestaurantsData as PulseRow[]) || []);
    const marketPartialLatest = ((data?.allRestaurantsPartialLatestData as PulseRow[]) || []);
    const marketYoYAll = (data?.allRestaurantsYoYData ?? []) as Parameters<typeof computeMarketPulseYoYComparisonData>[1];
    const baseMonths = data?.baseMonths ?? branchAll.length;
    const extrapolate = extrapolateToFullMonth;

    const comparisonData = computeMarketPulseComparisonData(
      branchAll,
      marketAll,
      baseMonths,
      extrapolate,
      marketPartialLatest,
    );
    const yoyComparisonData = computeMarketPulseYoYComparisonData(
      branchAll,
      marketYoYAll,
      baseMonths,
      extrapolate,
      marketPartialLatest,
      marketAll,
    );

    return { comparisonData, yoyComparisonData };
  }, [data, extrapolateToFullMonth]);

  if (forceComingSoonPreview) {
    return <MarketPulseComingSoon />;
  }

  if (isAdminLoading) {
    return (
      <div className="container-dashboard">
        <div className="card-base card-padding flex justify-center py-20">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return <MarketPulseComingSoon />;
  }

  const branchAll = ((data?.branchData as PulseRow[]) || []);
  const hasYoYData = hasMarketPulseYoYData(branchAll, processed.yoyComparisonData);
  const hasData = processed.comparisonData.tableRows.length > 0;

  return (
    <>
      {isLoading ? (
        <div className="container-dashboard">
          <div className="card-base card-padding flex justify-center py-20">
            <LoadingSpinner />
          </div>
        </div>
      ) : error ? (
        <div className="container-dashboard">
          <div className="card-base card-padding">
            <ErrorMessage message={MARKET_PULSE_DASHBOARD.errorFetch} onRetry={() => refetch()} />
          </div>
        </div>
      ) : (
        <>
          <div dir="rtl" className="container-dashboard">
            <div className="space-y-8 animate-enter-up">
              <header className="text-center space-y-3 pb-5 border-b border-border-primary">
                <h1 className="title-dashboard !mb-0">{MARKET_PULSE_DASHBOARD.name}</h1>
                <p className="text-sm md:text-base font-semibold text-status-success max-w-xl mx-auto leading-snug">
                  {MARKET_PULSE_DASHBOARD.hook}
                </p>
                <MarketPulseIntroBanner onOpenProductDoc={() => setIsProductDocumentOpen(true)} />
              </header>

              {!hasData ? (
                <div className="card-base card-padding">
                  <EmptyState message={MARKET_PULSE_DASHBOARD.emptyNoData} />
                </div>
              ) : (
                <>
                  <div className="card-base card-padding !py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="space-y-1.5 max-w-xl">
                        <p className="text-sm font-medium text-text-primary">{MARKET_PULSE_DASHBOARD.dataCoverageTitle}</p>
                        <p className="text-xs text-text-secondary leading-relaxed">
                          {MARKET_PULSE_DASHBOARD.dataCoverageDescription}
                        </p>
                      </div>
                      <ToggleButton
                        activeValue={extrapolateToFullMonth ? 'extrapolate' : 'recorded'}
                        onChange={(v) => setExtrapolateToFullMonth(v === 'extrapolate')}
                        options={[
                          { value: 'recorded', label: MARKET_PULSE_DASHBOARD.toggleRecorded },
                          { value: 'extrapolate', label: MARKET_PULSE_DASHBOARD.toggleExtrapolate },
                        ]}
                      />
                    </div>
                  </div>

                  <div id="section-market-pulse-kpi" className="dashboard-section-anchor">
                    <MarketPulseKpiSection
                      branchId={branchId}
                      customTimeRange={customTimeRange}
                      extrapolateToFullMonth={extrapolateToFullMonth}
                    />
                  </div>

                  <div id="section-market-pulse-table" className="dashboard-section-anchor">
                    <MarketPulseTableSection
                      branchId={branchId}
                      customTimeRange={customTimeRange}
                      extrapolateToFullMonth={extrapolateToFullMonth}
                      comparisonData={processed.comparisonData}
                    />
                  </div>

                  <div id="section-market-pulse-trend" className="dashboard-section-anchor">
                    <MarketPulseTrendSection
                      branchId={branchId}
                      customTimeRange={customTimeRange}
                      extrapolateToFullMonth={extrapolateToFullMonth}
                      comparisonData={processed.comparisonData}
                    />
                  </div>

                  {hasYoYData && (
                    <div id="section-market-pulse-yoy-table" className="dashboard-section-anchor">
                      <MarketPulseYoYTableSection
                        branchId={branchId}
                        customTimeRange={customTimeRange}
                        extrapolateToFullMonth={extrapolateToFullMonth}
                        comparisonData={processed.yoyComparisonData}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <MarketPulseProductDocModal
            isOpen={isProductDocumentOpen}
            onClose={() => setIsProductDocumentOpen(false)}
          />
        </>
      )}
    </>
  );
};

export default MarketPulseDashboard;
